import { Flex, Text, Image, Box } from "@chakra-ui/react";
import BgImage from "@/assets/bg.png";
import TitleHeader from "@/components/TitleHeader";
import { getOrder, Order, OrderType, Status } from "@/api/order";
import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { usdcInfo } from "@/constants/tokens";
import {
  accAdd,
  accDiv,
  accMul,
  formatAmountWithFixed,
  toFixed,
} from "@/utils/formatBalance";
import ReceiptTop from "./assets/receipt-top.png";
import ReceiptBottom from "./assets/receipt-bottom.png";
import BaseLogo from "@/assets/base.png";
import { shortenAddress } from "@/utils";
import { avatarImg, consumerEmoji } from "@/utils/avatars";
import { useTokenList } from "@/hooks/useTokens";
import dayjs from "dayjs";
import { useCurrencyExchange } from "@/hooks/useCurrencyExchange";
import { getDidName } from "@/utils/didName";

const Receipt = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [orderDetail, setOrderDetail] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const { usdToCurrency, currencyUnit } = useCurrencyExchange();
  const getOrderDetail = async () => {
    try {
      setLoading(true);
      const order = await getOrder(orderId!);
      setOrderDetail({
        ...order,
      });
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const tokens = useTokenList();

  const payToken = useMemo(() => {
    return tokens.find((token) => token.address === orderDetail?.spentCurrency);
  }, [tokens, orderDetail]);

  const { isConfirmed, allPayAmount, isNormalPay } = useMemo(() => {
    if (!orderDetail)
      return {
        isConfirmed: false,
        allPayAmount: "0",
        isNormalPay: false,
      };

    const allPayAmount = accAdd(
      accAdd(orderDetail.orderAmount, orderDetail.tipAmount),
      orderDetail.taxAmount
    );

    const isConfirmed = orderDetail.status === Status.Completed;
    const isNormalPay = orderDetail.orderType === OrderType.Normal;

    return {
      isConfirmed,
      allPayAmount,
      isNormalPay,
    };
  }, [orderDetail]);

  useEffect(() => {
    if (!orderId) return;
    getOrderDetail();
    const timer = setTimeout(() => {
      getOrderDetail();
    }, 3000);
    return () => {
      clearTimeout(timer);
    };
  }, [orderId]);

  return (
    <Flex
      sx={{
        width: "100%",
        minHeight: "100%",
        backgroundImage: `url(${BgImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        padding: "20px",
      }}
      flexDir={"column"}
      alignItems={"center"}
    >
      <TitleHeader
        onBack={() => navigate("/")}
        title="Receipt"
        color="#fff"
        showSwitch
      />
      <Flex
        sx={{
          width: "247px",
          background: `url(${ReceiptTop})`,
          backgroundSize: "100%",
          backgroundPosition: "0 0",
          backgroundRepeat: "no-repeat",
          marginTop: "30px",
          padding: "9px 7px 0px",
        }}
        flexDir={"column"}
      >
        <Flex
          sx={{
            width: "100%",
            backgroundColor: `#FFFFFF`,
            minHeight: "300px",
            color: "#333",
            padding: "30px 20px 40px",
          }}
          flexDir={"column"}
          align={"center"}
        >
          <Text
            sx={{
              fontSize: "40px",
              fontWeight: 900,
              fontFamily: "DIN-Black",
            }}
          >
            -
            {formatAmountWithFixed(
              usdToCurrency(allPayAmount),
              usdcInfo.decimals
            )}
          </Text>
          <Text
            sx={{
              color: "#333",
              fontSize: "24px",
              fontWeight: 500,
            }}
          >
            {currencyUnit}
          </Text>
          {isConfirmed && (
            <Flex
              sx={{
                width: "100%",
                marginTop: "22px",
                borderBottom: "1px dashed #848484",
                paddingBottom: "20px",
              }}
              flexDir={"column"}
            >
              <Text
                sx={{
                  color: "#848484",
                  fontSize: "14px",
                  fontWeight: 700,
                }}
              >
                From
              </Text>
              <Flex
                sx={{
                  marginTop: "10px",
                }}
                align={"center"}
              >
                <Text
                  sx={{
                    fontSize: "20px",
                    marginRight: "10px",
                  }}
                >
                  {consumerEmoji}
                </Text>
                <Box>
                  <Text
                    sx={{
                      color: "#848484",
                      fontSize: "12px",
                      fontWeight: 500,
                    }}
                  >
                    {shortenAddress(orderDetail?.sender?.address || "")}
                  </Text>
                </Box>
              </Flex>
            </Flex>
          )}
          {orderDetail && (
            <Flex
              sx={{
                width: "100%",
                marginTop: "22px",
                borderBottom: "1px dashed #848484",
                paddingBottom: "20px",
                position: "relative",
              }}
              flexDir={"column"}
            >
              <Text
                sx={{
                  color: "#848484",
                  fontSize: "14px",
                  fontWeight: 700,
                }}
              >
                To
              </Text>
              <Flex
                sx={{
                  marginTop: "10px",
                }}
                align={"center"}
              >
                <Text
                  sx={{
                    marginRight: "10px",
                    fontSize: "20px",
                  }}
                >
                  {avatarImg(orderDetail?.recipient?.avatar || "")}
                </Text>
                <Box>
                  <Text
                    sx={{
                      color: "#848484",
                      fontSize: "12px",
                      fontWeight: 500,
                    }}
                  >
                    {getDidName(orderDetail?.recipient?.didName)
                      ? getDidName(orderDetail?.recipient?.didName)
                      : shortenAddress(orderDetail?.recipient?.address || "")}
                  </Text>
                </Box>
              </Flex>
            </Flex>
          )}
          <Flex
            sx={{
              width: "100%",
              marginTop: "22px",
              borderBottom: "1px dashed #848484",
              paddingBottom: "20px",
              position: "relative",
            }}
            flexDir={"column"}
          >
            <Text
              sx={{
                fontSize: "14px",
                fontWeight: 700,
                color: "#848484",
              }}
            >
              Chain
            </Text>
            <Flex
              sx={{
                marginTop: "10px",
              }}
              align={"center"}
              justify={"space-between"}
            >
              <Image
                src={BaseLogo}
                sx={{
                  width: "20px",
                  height: "20px",
                  marginRight: "4px",
                }}
              ></Image>
              <Text
                sx={{
                  color: "#333",
                  fontSize: "14px",
                }}
              >
                Base
              </Text>
            </Flex>

            <Flex
              justify={"space-between"}
              sx={{
                marginTop: "10px",
              }}
            >
              <Text
                sx={{
                  color: "#848484",
                  fontSize: "14px",
                  fontWeight: 700,
                }}
              >
                Subtotal
              </Text>
              <Text
                sx={{
                  color: "#333",
                  fontSize: "14px",
                }}
              >
                -
                {formatAmountWithFixed(
                  usdToCurrency(orderDetail?.orderAmount),
                  usdcInfo.decimals
                )}{" "}
                {currencyUnit}
              </Text>
            </Flex>
            {isNormalPay && (
              <Flex
                justify={"space-between"}
                sx={{
                  marginTop: "10px",
                }}
              >
                <Text
                  sx={{
                    color: "#848484",
                    fontSize: "14px",
                    fontWeight: 700,
                  }}
                >
                  Tax
                </Text>
                <Box
                  sx={{
                    textAlign: "right",
                  }}
                >
                  <Text
                    sx={{
                      color: "#333",
                      fontSize: "14px",
                    }}
                  >
                    -
                    {formatAmountWithFixed(
                      usdToCurrency(orderDetail?.taxAmount),
                      usdcInfo.decimals
                    )}{" "}
                    {currencyUnit}
                  </Text>
                  <Text
                    sx={{
                      fontSize: "12px",
                    }}
                  >
                    {orderDetail?.taxRate}%
                  </Text>
                </Box>
              </Flex>
            )}
            {isNormalPay && (
              <Flex
                justify={"space-between"}
                sx={{
                  marginTop: "10px",
                }}
              >
                <Text
                  sx={{
                    color: "#848484",
                    fontSize: "14px",
                    fontWeight: 700,
                  }}
                >
                  Tips
                </Text>
                <Box
                  sx={{
                    textAlign: "right",
                  }}
                >
                  <Text
                    sx={{
                      color: "#333",
                      fontSize: "14px",
                    }}
                  >
                    -
                    {formatAmountWithFixed(
                      usdToCurrency(orderDetail?.tipAmount),
                      usdcInfo.decimals
                    )}{" "}
                    {currencyUnit}
                  </Text>
                  <Text
                    sx={{
                      fontSize: "12px",
                    }}
                  >
                    {toFixed(
                      accMul(
                        accDiv(
                          orderDetail?.tipAmount,
                          orderDetail?.orderAmount
                        ),
                        100
                      ),
                      2
                    )}
                    %{" "}
                  </Text>
                </Box>
              </Flex>
            )}

            <Flex
              justify={"space-between"}
              sx={{
                marginTop: "10px",
              }}
            >
              <Text
                sx={{
                  color: "#848484",
                  fontSize: "14px",
                  fontWeight: 700,
                }}
              >
                Total
              </Text>
              <Box
                sx={{
                  textAlign: "right",
                }}
              >
                <Text
                  sx={{
                    color: "#333",
                    fontSize: "16px",
                    fontWeight: 700,
                  }}
                >
                  -
                  {formatAmountWithFixed(
                    usdToCurrency(
                      accAdd(
                        orderDetail?.orderAmount,
                        accAdd(orderDetail?.tipAmount, orderDetail?.taxAmount)
                      )
                    ),
                    usdcInfo.decimals
                  )}{" "}
                  {currencyUnit}
                </Text>
                {isConfirmed && (
                  <Text
                    sx={{
                      fontSize: "12px",
                    }}
                  >
                    -
                    {formatAmountWithFixed(
                      orderDetail?.spentAmount,
                      payToken?.decimals
                    )}{" "}
                    {payToken?.symbol}
                  </Text>
                )}
              </Box>
            </Flex>
          </Flex>
          {isConfirmed && (
            <Flex
              sx={{
                marginTop: "15px",
                width: "100%",
              }}
              justify={"space-between"}
            >
              <Text
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#848484",
                }}
              >
                Time
              </Text>
              <Box
                sx={{
                  textAlign: "right",
                }}
              >
                <Text
                  sx={{
                    color: "#333",
                    fontSize: "14px",
                  }}
                >
                  {dayjs(orderDetail?.updatedAt).format("YYYY/MM/DD HH:mm")}
                </Text>
                <Text
                  sx={{
                    color: "#848484",
                    fontSize: "12px",
                    fontWeight: 400,
                    fontFamily: "DIN-LightItalic",
                    marginTop: "4px",
                  }}
                >
                  Sent {dayjs(orderDetail?.updatedAt).fromNow()}
                </Text>{" "}
                <Text
                  sx={{
                    color: isConfirmed ? "#00B327" : "#848484",
                    fontSize: "12px",
                    fontWeight: 500,
                    width: "100%",
                    marginTop: "4px",
                  }}
                >
                  {isConfirmed ? "Confirmed" : "Pending"}
                </Text>
              </Box>
            </Flex>
          )}
        </Flex>
        <Image
          src={ReceiptBottom}
          sx={{
            width: "100%",
          }}
        ></Image>
      </Flex>
    </Flex>
  );
};

export default Receipt;
